/**
 * Merlin order cards — the visual layer for the two order-placement tools.
 *
 * ReadbackCard renders a `propose_order_readback` frame: the whole order at a
 * glance (skimmable), unresolved questions highlighted, and a speak button
 * that reads the agent's summary aloud (same TTS path as the voice rundown).
 * The user confirms by TALKING/TYPING (the agent asked a question) — the card
 * is the thing they skim, per the approved conversation design.
 *
 * DraftCard renders a successful `create_draft_order`: the NOT-SUBMITTED
 * state, what was created, and the Review button that opens the shared order
 * modal right in the conversation.
 */

import styles from './QcpOrderCards.module.css';
import type { DraftCardData, OrderReadbackData } from './qualityCopilotOrderCards';

function money(v: number | null): string {
  return v == null
    ? '—'
    : v.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function ReadbackCard({
  data,
  onSpeak,
  speaking,
}: {
  data: OrderReadbackData;
  onSpeak: () => void;
  speaking: boolean;
}) {
  return (
    <div className={styles.card} data-testid="qcp-readback-card">
      <div className={styles.head}>
        <span className={styles.headIcon} aria-hidden="true">📋</span>
        <div className={styles.headText}>
          <span className={styles.title}>Order read-back — please confirm</span>
          <span className={styles.subtitle}>
            {data.customerName ?? 'Customer to be assigned'}
            {data.customerNo ? ` (${data.customerNo})` : ''}
            {data.poNumber ? ` · PO ${data.poNumber}` : ''}
          </span>
        </div>
        {data.spokenSummary && (
          <button
            type="button"
            className={styles.speakBtn}
            onClick={onSpeak}
            aria-label={speaking ? 'Stop reading the order aloud' : 'Read the order aloud'}
          >
            {speaking ? '❚❚' : '🔊'}
          </button>
        )}
      </div>
      <div className={styles.lines}>
        {data.lines.map((li, i) => (
          <div key={`${li.itemCode ?? 'line'}-${i}`} className={styles.line}>
            <span className={styles.lineQty}>
              {li.quantity ?? '?'} {li.unit ?? ''}
            </span>
            <span className={styles.lineDesc}>
              {li.itemCode ?? 'item to confirm'}
              {li.description ? ` — ${li.description}` : ''}
              {li.needsReview && <span className={styles.flag}> · needs review</span>}
            </span>
            <span className={styles.linePrice}>
              {li.unitPrice != null ? `${money(li.unitPrice)}/ea` : ''}
            </span>
          </div>
        ))}
      </div>
      {data.computedSubtotal != null && (
        <div className={styles.total}>Subtotal {money(data.computedSubtotal)}</div>
      )}
      {data.unresolved.length > 0 && (
        <div className={styles.unresolved}>
          <span className={styles.unresolvedLabel}>Still need from you:</span>
          <ul>
            {data.unresolved.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
        </div>
      )}
      <div className={styles.hint}>
        Reply to confirm or correct — nothing is created until you say so.
      </div>
    </div>
  );
}

export function DraftCard({
  data,
  onReview,
}: {
  data: DraftCardData;
  onReview: (orderId: string) => void;
}) {
  return (
    <div className={styles.card} data-testid="qcp-draft-card">
      <div className={styles.head}>
        <span className={styles.headIcon} aria-hidden="true">✅</span>
        <div className={styles.headText}>
          <span className={styles.title}>Draft order created</span>
          <span className={styles.subtitle}>
            {data.lineCount != null
              ? `${data.lineCount} line${data.lineCount === 1 ? '' : 's'}`
              : 'Draft'}
            {data.flaggedCount != null && data.flaggedCount > 0
              ? ` · ${data.flaggedCount} flagged for the orders team`
              : ''}
          </span>
        </div>
        <span className={data.held ? styles.badgeHeld : styles.badgeDraft}>
          {data.held ? 'HELD FOR REVIEW' : 'NOT SUBMITTED'}
        </span>
      </div>
      <div className={styles.hint}>
        The orders team reviews and submits every draft — nothing goes to Sage
        without them.
      </div>
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.reviewBtn}
          onClick={() => onReview(data.orderId)}
        >
          Review order ›
        </button>
      </div>
    </div>
  );
}
